import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RootState, AppDispatch } from '../store';
import type { AdminState } from '../store/slices/adminSlice';
import { queryKeys, medicalQueryOptions } from '../services/queryClient';
import { apiClient } from '../services/api/base';

// Type interfaces for better type safety
interface LoginCredentials {
  email: string;
  password: string;
}

interface PatientFilters {
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
}

interface ReportFilters {
  patient_id?: string;
  date_from?: string;
  date_to?: string;
  status?: string;
}

interface FormDraftData {
  [key: string]: any; // This will be replaced with proper form schema types
}

interface PatientData {
  name: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  medical_id?: string;
  [key: string]: any; // For additional dynamic fields
}

interface ReportData {
  title: string;
  content: string;
  patient_id: string;
  template_id?: string;
  status?: 'draft' | 'completed' | 'reviewed';
  [key: string]: any; // For additional dynamic fields
}

interface LoincTerm {
  loinc_num: string;
  component: string;
  property: string;
  time_aspct: string;
  system: string;
  scale_typ: string;
  method_typ: string;
  class: string;
  long_common_name?: string;
  shortname?: string;
}

// Utility function for handling API responses
const handleApiResponse = async (response: Response, operation: string) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `${operation} failed`);
  }
  const data = await response.json();
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid response format');
  }
  return data;
};

// Typed hooks for Redux
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();
export const useAppSelector = <T>(selector: (state: RootState) => T): T =>
  useSelector(selector);

// Auth hooks with hybrid JWT/Cookie support
export const useAuth = () => {
  const dispatch = useAppDispatch();
  const authState = useAppSelector((state) => state.auth);
  
  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
        credentials: 'include', // Include cookies for hybrid auth
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Login failed');
      }
      const data = await response.json();
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format');
      }
      return data;
    },
    onSuccess: (data) => {
      // Update Redux state with user data
      dispatch({ type: 'auth/loginSuccess', payload: data });
    },
  });
  
  const logoutMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/v1/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Logout failed');
      }
      const data = await response.json();
      return data;
    },
    onSuccess: () => {
      dispatch({ type: 'auth/logout' });
    },
  });
  
  return {
    ...authState,
    login: loginMutation.mutate,
    logout: logoutMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
  };
};

// Patients hooks with DICOM integration
export const usePatients = (
  filters?: PatientFilters,
  options?: { enabled?: boolean }
) => {
  // const dispatch = useAppDispatch(); // TODO: Use for mutations
  const patientsState = useAppSelector((state) => state.patients);
  
  const patientsQuery = useQuery({
    queryKey: queryKeys.patients.list(filters),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
          }
        });
      }
      const response = await apiClient.get(`/api/v1/patients?${queryParams}`);
      return response.data;
    },
    // Keep previous data while fetching new results to prevent white screen flash
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
    ...medicalQueryOptions.patientData,
  });
  
  const createPatientMutation = useMutation({
    mutationFn: async (patientData: PatientData) => {
      const response = await apiClient.post('/api/v1/patients', patientData);
      return response.data;
    },
    onSuccess: () => {
      patientsQuery.refetch();
    },
  });
  
  return {
    ...patientsState,
    patients: patientsQuery.data?.patients || [],
    total: patientsQuery.data?.total || patientsQuery.data?.patients?.length || 0,
    // Only true on initial load when no data exists
    isLoading: patientsQuery.isLoading && !patientsQuery.isPlaceholderData,
    // True when fetching new data (for search indicator)
    isFetching: patientsQuery.isFetching,
    // True when showing stale/placeholder data while fetching
    isPlaceholderData: patientsQuery.isPlaceholderData,
    error: patientsQuery.error?.message,
    createPatient: createPatientMutation.mutate,
    isCreating: createPatientMutation.isPending,
    refetch: patientsQuery.refetch,
  };
};

// Patient details with studies hook  
export const usePatientDetails = (patientId?: string) => {
  const patientQuery = useQuery({
    queryKey: queryKeys.patients.detail(patientId!),
    queryFn: async () => {
      const response = await apiClient.get(`/api/v1/patients/${patientId}`);
      return response.data;
    },
    enabled: !!patientId,
    ...medicalQueryOptions.patientData,
  });

  return {
    patient: patientQuery.data,
    isLoading: patientQuery.isLoading,
    error: patientQuery.error?.message,
    refetch: patientQuery.refetch,
  };
};

// Patient reports hook
export const usePatientReports = (patientId?: string) => {
  const reportsQuery = useQuery({
    queryKey: queryKeys.reports.byPatient(patientId!),
    queryFn: async () => {
      const response = await apiClient.get(`/api/v1/patients/${patientId}/reports`);
      return response.data;
    },
    enabled: !!patientId,
    ...medicalQueryOptions.reports,
  });

  return {
    reports: reportsQuery.data?.reports || [],
    isLoading: reportsQuery.isLoading,
    error: reportsQuery.error?.message,
    refetch: reportsQuery.refetch,
  };
};

// DICOM hooks preserving existing functionality
export const useDicom = (studyId?: string) => {
  const dispatch = useAppDispatch();
  const dicomState = useAppSelector((state) => state.dicom);
  
  const studyQuery = useQuery({
    queryKey: queryKeys.dicom.study(studyId!),
    queryFn: async () => {
      const response = await fetch(`/api/v1/dicom/studies/${studyId}`, {
        credentials: 'include',
      });
      return handleApiResponse(response, 'Fetch DICOM study');
    },
    enabled: !!studyId,
    ...medicalQueryOptions.dicomData,
  });
  
  const imagesQuery = useQuery({
    queryKey: queryKeys.dicom.images(studyId!),
    queryFn: async () => {
      const response = await fetch(`/api/v1/dicom/studies/${studyId}/images`, {
        credentials: 'include',
      });
      return handleApiResponse(response, 'Fetch DICOM images');
    },
    enabled: !!studyId,
    ...medicalQueryOptions.dicomData,
  });
  
  const extractMeasurementsMutation = useMutation({
    mutationFn: async (imageId: string) => {
      const response = await fetch(`/api/v1/dicom/images/${imageId}/measurements`, {
        method: 'POST',
        credentials: 'include',
      });
      return handleApiResponse(response, 'Extract measurements');
    },
    onSuccess: (data) => {
      dispatch({ type: 'dicom/addMeasurements', payload: data.measurements });
    },
  });
  
  return {
    // Safely spread dicomState with fallback
    ...(dicomState || {}),
    study: studyQuery.data,
    images: imagesQuery.data?.images || [],
    isLoadingStudy: studyQuery.isLoading,
    isLoadingImages: imagesQuery.isLoading,
    studyError: studyQuery.error?.message,
    imagesError: imagesQuery.error?.message,
    extractMeasurements: extractMeasurementsMutation.mutate,
    isExtracting: extractMeasurementsMutation.isPending,
  };
};

// Reports hooks with form state management
export const useReports = (filters?: ReportFilters) => {
  // const dispatch = useAppDispatch(); // TODO: Use for mutations
  const reportsState = useAppSelector((state) => state.reports);
  const queryClient = useQueryClient();
  
  const reportsQuery = useQuery({
    queryKey: queryKeys.reports.list(filters),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
          }
        });
      }
      const response = await fetch(`/api/v1/reports?${queryParams}`, {
        credentials: 'include',
      });
      return handleApiResponse(response, 'Fetch reports');
    },
    ...medicalQueryOptions.reports,
  });
  
  const templatesQuery = useQuery({
    queryKey: queryKeys.reports.templates,
    queryFn: async () => {
      const response = await fetch('/api/v1/reports/templates', {
        credentials: 'include',
      });
      return handleApiResponse(response, 'Fetch templates');
    },
    ...medicalQueryOptions.terminology, // Templates change rarely
  });
  
  const createReportMutation = useMutation({
    mutationFn: async (reportData: ReportData) => {
      const response = await fetch('/api/v1/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportData),
        credentials: 'include',
      });
      return handleApiResponse(response, 'Create report');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
    },
  });
  
  const updateReportMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ReportData> }) => {
      const response = await fetch(`/api/v1/reports/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        credentials: 'include',
      });
      return handleApiResponse(response, 'Update report');
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.reports.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
    },
  });
  
  return {
    ...reportsState,
    reports: reportsQuery.data?.items || [],
    templates: templatesQuery.data || {},
    total: reportsQuery.data?.total || 0,
    isLoading: reportsQuery.isLoading || templatesQuery.isLoading,
    error: reportsQuery.error?.message || templatesQuery.error?.message,
    createReport: createReportMutation.mutate,
    updateReport: updateReportMutation.mutate,
    isCreating: createReportMutation.isPending,
    isUpdating: updateReportMutation.isPending,
    refetch: reportsQuery.refetch,
  };
};

// LOINC hooks with bilingual support
export const useLoinc = () => {
  const dispatch = useAppDispatch();
  const loincState = useAppSelector((state) => state.loinc);
  const queryClient = useQueryClient();
  
  const searchMutation = useMutation({
    mutationFn: async (params: { query: string; language?: 'es' | 'en' }) => {
      const searchParams = new URLSearchParams({
        q: params.query,
        language: params.language || loincState.language,
      });
      
      const response = await fetch(`/api/v1/loinc/search?${searchParams}`, {
        credentials: 'include',
      });
      return handleApiResponse(response, 'LOINC search');
    },
    onSuccess: (data, variables) => {
      dispatch({ type: 'loinc/setSearchResults', payload: data });
      dispatch({ type: 'loinc/setSearchQuery', payload: variables.query });
      // Cache successful searches
      queryClient.setQueryData(
        queryKeys.loinc.search(variables.query, variables.language),
        data
      );
    },
  });
  
  const getTermQuery = useQuery({
    queryKey: queryKeys.loinc.term(loincState.searchQuery),
    queryFn: async () => {
      if (!loincState.searchQuery) return null;
      
      const response = await fetch(`/api/v1/loinc/terms/${loincState.searchQuery}`, {
        credentials: 'include',
      });
      return handleApiResponse(response, 'Fetch LOINC term');
    },
    enabled: !!loincState.searchQuery,
    ...medicalQueryOptions.terminology,
  });
  
  const statsQuery = useQuery({
    queryKey: queryKeys.loinc.stats,
    queryFn: async () => {
      const response = await fetch('/api/v1/loinc/stats', {
        credentials: 'include',
      });
      return handleApiResponse(response, 'Fetch LOINC stats');
    },
    ...medicalQueryOptions.terminology,
  });
  
  return {
    ...loincState,
    searchTerms: searchMutation.mutate,
    isSearching: searchMutation.isPending,
    searchError: searchMutation.error?.message,
    termDetails: getTermQuery.data,
    stats: statsQuery.data,
    isLoadingStats: statsQuery.isLoading,
    addSelectedTerm: (term: LoincTerm) => dispatch({ type: 'loinc/addSelectedTerm', payload: term }),
    removeSelectedTerm: (loincNum: string) => dispatch({ type: 'loinc/removeSelectedTerm', payload: loincNum }),
    setLanguage: (language: 'es' | 'en') => dispatch({ type: 'loinc/setLanguage', payload: language }),
  };
};

// Admin hooks for system management
export const useAdmin = () => {
  // const dispatch = useAppDispatch(); // TODO: Use for mutations
  const adminState = useAppSelector((state) => state.admin) as AdminState;
  
  // Type guard to ensure adminState is properly initialized
  if (!adminState?.filters) {
    return {
      users: [],
      stats: null,
      isLoading: false,
      error: 'Admin state not properly initialized',
      refetchUsers: () => Promise.resolve(),
      refetchStats: () => Promise.resolve(),
    };
  }
  
  const usersQuery = useQuery({
    queryKey: queryKeys.admin.users(adminState.filters.users),
    queryFn: async () => {
      // Filter out null values for URLSearchParams
      const filterParams = Object.fromEntries(
        Object.entries(adminState.filters.users).filter(([_, value]) => value !== null)
      ) as Record<string, string>;
      
      const queryParams = new URLSearchParams({
        page: adminState.currentPage.toString(),
        size: adminState.pageSize.toString(),
        ...filterParams,
      });
      
      const response = await fetch(`/api/v1/admin/users?${queryParams}`, {
        credentials: 'include',
      });
      return handleApiResponse(response, 'Fetch users');
    },
    ...medicalQueryOptions.patientData,
  });
  
  const statsQuery = useQuery({
    queryKey: queryKeys.admin.stats,
    queryFn: async () => {
      const response = await fetch('/api/v1/admin/stats', {
        credentials: 'include',
      });
      return handleApiResponse(response, 'Fetch stats');
    },
    ...medicalQueryOptions.terminology,
  });
  
  return {
    // Safely spread adminState with fallback
    ...(adminState || {}),
    users: usersQuery.data?.users || [],
    stats: statsQuery.data,
    isLoading: usersQuery.isLoading || statsQuery.isLoading,
    error: usersQuery.error?.message || statsQuery.error?.message,
    refetchUsers: usersQuery.refetch,
    refetchStats: statsQuery.refetch,
  };
};

// Medical form state management hook
export const useMedicalForm = (formId: string) => {
  const dispatch = useAppDispatch();
  const draftData = useAppSelector((state) => state.reports.draftData[formId] || {});
  
  const saveDraft = (data: FormDraftData) => {
    dispatch({
      type: 'reports/updateDraftData',
      payload: { formId, data },
    });
    
    // Persist to localStorage for recovery
    try {
      const drafts = JSON.parse(localStorage.getItem('gynhub-form-drafts') || '{}');
      drafts[formId] = {
        data,
        timestamp: Date.now(),
        version: '1.0.0',
      };
      localStorage.setItem('gynhub-form-drafts', JSON.stringify(drafts));
    } catch (error) {
      console.warn('Failed to save form draft:', error);
    }
  };
  
  const loadDraft = () => {
    try {
      const drafts = JSON.parse(localStorage.getItem('gynhub-form-drafts') || '{}');
      const draft = drafts[formId];
      if (draft && draft.data) {
        dispatch({
          type: 'reports/updateDraftData',
          payload: { formId, data: draft.data },
        });
        return draft.data;
      }
    } catch (error) {
      console.warn('Failed to load form draft:', error);
    }
    return null;
  };
  
  const clearDraft = () => {
    dispatch({
      type: 'reports/clearDraftData',
      payload: formId,
    });
    
    try {
      const drafts = JSON.parse(localStorage.getItem('gynhub-form-drafts') || '{}');
      delete drafts[formId];
      localStorage.setItem('gynhub-form-drafts', JSON.stringify(drafts));
    } catch (error) {
      console.warn('Failed to clear form draft:', error);
    }
  };
  
  return {
    draftData,
    saveDraft,
    loadDraft,
    clearDraft,
  };
};
